/**
 * Which language a request renders in. Pure, so the order is testable
 * without a request scope; i18n/request.ts feeds it the real cookies and
 * headers.
 *
 * A signed-in user reads in the language their account stores, carried on the
 * session token as `preferredLanguage` (null until they, or the sign-in page,
 * chose one — that renders as English). Nothing else is consulted for them:
 * no app-wide cookie, so one account's choice can never leak into another's
 * sign-in page.
 *
 * A signed-out request (the sign-in, code and signup pages) reads the
 * `SIGNIN_LOCALE` cookie the sign-in page's own selector writes, else the
 * browser's Accept-Language, else English.
 */
import { defaultLocale, locales, type Locale } from './config';

export const SIGNIN_LOCALE_COOKIE = 'SIGNIN_LOCALE';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (locales as readonly string[]).includes(value);
}

function localeForTag(tag: string): Locale | null {
  if (tag === 'ko' || tag.startsWith('ko-')) return 'ko';
  if (tag === 'en' || tag.startsWith('en-')) return 'en';
  // Traditional Chinese is a script, which the tag carries either explicitly
  // (zh-Hant, zh-Hant-TW) or by a region that writes it (Taiwan, Hong Kong,
  // Macau). A bare `zh` or a Simplified region is not a match.
  if (tag === 'zh' || tag.startsWith('zh-')) {
    const subtags = tag.split('-').slice(1);
    if (subtags.some((s) => s === 'hant' || s === 'tw' || s === 'hk' || s === 'mo')) return 'zh-Hant';
  }
  return null;
}

/** The first locale the browser asks for that the app has, in the header's
 *  quality order; null when none of the ranges matches. */
export function localeFromAcceptLanguage(header: string | null | undefined): Locale | null {
  if (!header) return null;
  const ranges = header
    .split(',')
    .map((range, index) => {
      const [tag, ...params] = range.trim().split(';');
      const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      const weight = q ? Number(q.slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), weight: Number.isFinite(weight) ? weight : 0, index };
    })
    .filter((r) => r.tag !== '' && r.weight > 0)
    .sort((a, b) => b.weight - a.weight || a.index - b.index);
  for (const { tag } of ranges) {
    const match = localeForTag(tag);
    if (match) return match;
  }
  return null;
}

export interface LocaleSources {
  /** The decoded session token, or null when the request carries no valid one. */
  session: { preferredLanguage?: unknown } | null;
  /** The `SIGNIN_LOCALE` cookie, if present. */
  signinCookie: string | undefined;
  acceptLanguage: string | null | undefined;
}

export function resolveLocale({ session, signinCookie, acceptLanguage }: LocaleSources): Locale {
  if (session) return isLocale(session.preferredLanguage) ? session.preferredLanguage : defaultLocale;
  if (isLocale(signinCookie)) return signinCookie;
  return localeFromAcceptLanguage(acceptLanguage) ?? defaultLocale;
}
